import type {
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetWorkspaceSelectionFn,
  PrimeMemberByPublicIdFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import type { Member } from '@/features/iam/members/Member'
import type { Job } from '@/features/jobs/Job'
import type { FetchJobFn, FetchJobsFn, SearchJobsFn } from '@/features/jobs'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { Membership } from '@/features/iam/workspaces/Membership'
import type { GetDocumentDownloadDelegate } from '@/features/shell/platform/types'
import type { Document } from '@/features/documents/Document'
import type { DocumentType } from '@/features/documents/DocumentType'
import type { SearchDocumentsQuery } from '@/features/documents/DocumentStore'
import type { DocumentPickerViewModel } from '@/features/documents/DocumentPicker/DocumentPickerViewModel'
import type {
  UploadBarViewModel,
  UploadBarViewModelOptions,
} from '@/features/documents/UploadBarViewModel'
import type { DocumentCreationModalViewModel } from '@/features/documents/DocumentCreationModal/DocumentCreationModalViewModel'
import type { DocumentRequestsViewModel } from '@/features/documents/DocumentRequestsViewModel'
import type {
  JobDocumentListViewModel,
  JobDocumentListViewModelOptions,
} from '@/features/documents/JobDocumentListViewModel'
import type { DocumentsTabViewModel } from '@/features/documents/DocumentsTabViewModel'
import type { Id } from '@/infra/id'
import type { CursorPaged } from '@/infra/paging'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import { toStream } from '@/utils/mobx-util'
import type { Observable } from 'rxjs'

/**
 * Props for priming a document.
 */
export interface PrimeDocumentProps {
  id: Id
}

/**
 * Primes a document in the collection and refreshes it in the background.
 */
export type PrimeDocumentFn = (
  props: PrimeDocumentProps,
) => Document | undefined

/**
 * Streams the loaded documents linked to the job.
 */
export type JobDocumentsFn = (jobId: Id) => Observable<Document[]>

/**
 * Searches documents.
 */
export type SearchDocumentsFn = (
  query: SearchDocumentsQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<Document>>

/**
 * Archives the document, resolving with it once refetched.
 */
export type ArchiveDocumentFn = (
  document: Document,
) => Promise<Document | undefined>

/**
 * Downloads the document's latest version, or opens it in the browser when
 * `inline` is set.
 */
export type DownloadDocumentFn = (
  document: Document,
  inline?: boolean,
) => Promise<void>

/**
 * Downloads the documents as a single zip file.
 */
export type DownloadDocumentsFn = (documentIds: Id[]) => Promise<void>

/**
 * Confirms the document's client data for the job.
 */
export type ConfirmDocumentFn = (documentId: Id, jobId: Id) => Promise<void>

/**
 * Options for prompting to delete a document.
 */
export interface PromptDeleteDocumentOptions {
  document: Document
  signal?: AbortSignal
}

/**
 * Asks the user to confirm, then permanently deletes the document.
 * Resolves with whether the document was deleted.
 */
export type PromptDeleteDocumentFn = (
  options: PromptDeleteDocumentOptions,
) => Promise<boolean>

/**
 * Options for prompting to unlink a document from a job.
 */
export interface PromptUnlinkDocumentOptions {
  document: Document
  jobId: Id
  signal?: AbortSignal
}

/**
 * Asks the user to confirm, then unlinks the document from the job.
 * Resolves with whether the document was unlinked.
 */
export type PromptUnlinkDocumentFn = (
  options: PromptUnlinkDocumentOptions,
) => Promise<boolean>

/**
 * Shows the document's details, optionally with admin rights.
 */
export type ShowDocumentDetailsFn = (
  document: Document,
  viewAsAdmin?: boolean,
) => unknown

/**
 * Primes a document type by its short ID and refreshes it in the background.
 */
export type PrimeDocumentTypeByShortIdFn = (
  shortId?: string,
  name?: string,
) => DocumentType | undefined

/**
 * Lists the document types.
 */
export type FetchDocumentTypesFn = () => Promise<DocumentType[]>

/**
 * Creates a picker for linking the current member's documents to the job.
 */
export type DocumentPickerViewModelFactory = (
  job: Job,
) => DocumentPickerViewModel

/**
 * Creates an upload bar that validates and uploads files.
 */
export type UploadBarViewModelFactory = (
  options?: UploadBarViewModelOptions,
) => UploadBarViewModel

/**
 * Creates a modal for choosing the types of files before uploading them
 * to the job.
 */
export type DocumentCreationModalViewModelFactory = (
  files: File[],
  getDocumentTypes: () => DocumentType[],
  job: Job,
  uploadBarVm: UploadBarViewModel,
) => DocumentCreationModalViewModel

/**
 * Creates the list of a job's documents.
 */
export type JobDocumentListViewModelFactory = (
  options: JobDocumentListViewModelOptions,
) => JobDocumentListViewModel

/**
 * Creates the document requests for a job's questions.
 */
export type DocumentRequestsViewModelFactory = (
  job: Job,
) => DocumentRequestsViewModel

/**
 * The user whose documents are managed, and the memberships that decide which
 * of their workspaces the current user may manage documents in.
 */
export interface DocumentManagementViewModelOptions {
  userPublicId: () => Id
  userMemberships: () => Membership[]
  currentUserMemberships: () => Membership[]
}

/**
 * Creates the documents tab for managing a user's documents.
 */
export type DocumentsTabViewModelFactory = (
  options: DocumentManagementViewModelOptions,
) => DocumentsTabViewModel

/**
 * Options for {@link DocumentsFeature}.
 */
export interface DocumentsFeatureOptions {
  token$: Observable<string>
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
  getDocumentDownloadDelegate: GetDocumentDownloadDelegate
  currentMember$: Observable<Member>
  getCurrentMember: GetCurrentMemberFn
  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn
  getWorkspaceSelection: GetWorkspaceSelectionFn
  primeMemberByPublicId: PrimeMemberByPublicIdFn
  primeWorkspace: PrimeWorkspaceFn
  fetchJob: FetchJobFn
  fetchJobs: FetchJobsFn
  searchJobs: SearchJobsFn
}

/**
 * Feature relating to documents.
 */
export type DocumentsFeature = ReturnType<typeof DocumentsFeature>

export function DocumentsFeature(options: DocumentsFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (m) => m.InternalDocumentsFeature(options),
  )

  const primeDocument = internal.lazy<PrimeDocumentFn>((m) => m.primeDocument)
  const searchDocuments: SearchDocumentsFn = internal.wrap(
    (m) => m.searchDocuments,
  )
  const archiveDocument: ArchiveDocumentFn = internal.wrap(
    (m) => m.archiveDocument,
  )
  const downloadDocument: DownloadDocumentFn = internal.wrap(
    (m) => m.downloadDocument,
  )
  const downloadDocuments: DownloadDocumentsFn = internal.wrap(
    (m) => m.downloadDocuments,
  )
  const confirmDocument: ConfirmDocumentFn = internal.wrap(
    (m) => m.confirmDocument,
  )
  const showDocumentDetails: ShowDocumentDetailsFn = internal.wrap(
    (m) => m.showDocumentDetails,
  )

  const jobDocuments: JobDocumentsFn = (jobId) =>
    internal.deferUntilLoaded((m) =>
      toStream(() => m.documentStore.forJob(jobId), true),
    )

  const documentManualVerificationRequired$ = internal.defer(
    (m) => m.documentStore.manualVerificationRequired$,
  )

  const primeDocumentTypeByShortId =
    internal.lazy<PrimeDocumentTypeByShortIdFn>(
      (m) => m.primeDocumentTypeByShortId,
    )
  const documentTypes$ = internal.deferUntilLoaded((m) =>
    toStream(() => m.documentTypeStore.types.slice(), true),
  )
  const fetchDocumentTypes: FetchDocumentTypesFn = internal.wrap(
    (m) => m.fetchDocumentTypes,
  )

  const createDocumentPickerViewModel =
    internal.lazy<DocumentPickerViewModelFactory>(
      (m) => m.createDocumentPickerViewModel,
    )
  const createUploadBarViewModel = internal.lazy<UploadBarViewModelFactory>(
    (m) => m.createUploadBarViewModel,
  )
  const createDocumentCreationModalViewModel =
    internal.lazy<DocumentCreationModalViewModelFactory>(
      (m) => m.createDocumentCreationModalViewModel,
    )
  const createJobDocumentListViewModel =
    internal.lazy<JobDocumentListViewModelFactory>(
      (m) => m.createJobDocumentListViewModel,
    )
  const createDocumentRequestsViewModel =
    internal.lazy<DocumentRequestsViewModelFactory>(
      (m) => m.createDocumentRequestsViewModel,
    )
  const createDocumentsTabViewModel =
    internal.lazy<DocumentsTabViewModelFactory>(
      (m) => m.createDocumentsTabViewModel,
    )

  return {
    // Internal
    internal,

    // Public
    primeDocument,
    searchDocuments,
    archiveDocument,
    downloadDocument,
    downloadDocuments,
    confirmDocument,
    showDocumentDetails,
    jobDocuments,
    documentManualVerificationRequired$,
    primeDocumentTypeByShortId,
    documentTypes$,
    fetchDocumentTypes,
    createDocumentPickerViewModel,
    createUploadBarViewModel,
    createDocumentCreationModalViewModel,
    createJobDocumentListViewModel,
    createDocumentRequestsViewModel,
    createDocumentsTabViewModel,
  }
}
